import React, { useState } from 'react'

import { Avatar, Group, Select, Table, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { showAlert, showError } from '#components/Modals'
import { Spinner } from '#components/Spinner'
import { useMst } from '#state'
import { IOrgMember } from '#state/types'
import { capitalize, getAvatarUrl } from '#util'

import { MemberAction } from './MemberAction'

import styles from './MemberTableRow.module.scss'

const ROLES_DATA = [
  { value: 'contributor', label: 'Contributor' },
  { value: 'admin', label: 'Admin' },
]

export const MemberTableRow = observer(function MemberTableRow({
  member,
}: {
  member: IOrgMember
}) {
  const { user, environment } = useMst()
  const [value, setValue] = useState(member.role)
  const isContributor = user.isCurrentOrgContributor

  // Sync local state when member.role changes (e.g., after ownership transfer)
  React.useEffect(() => {
    setValue(member.role)
  }, [member.role])

  const updateUserRole = async (role: string | null) => {
    if (member.role === role || role === null) return
    try {
      setValue(role)
      role === 'contributor' ? await member.demote() : await member.promote()
      showAlert({
        title: 'Success',
        children: 'Your changes have been saved',
      })
    } catch (e) {
      setValue(capitalize(member.role))
      showError({ message: 'Failed to update user' })
    }
  }

  const displayRole =
    !isContributor && !member.isOwner ? (
      <Select
        data={ROLES_DATA}
        value={value}
        variant="default"
        onChange={(role) => updateUserRole(role)}
        withCheckIcon={false}
      />
    ) : (
      capitalize(member.role)
    )

  return (
    <Table.Tr>
      <Table.Td>
        {member.isChangingRole && <Spinner noOverlay={true} delayMs={300} />}
        <Group gap="sm">
          {member.name && (
            <Avatar
              size={40}
              src={getAvatarUrl(member.avatar, environment.config)}
              radius={40}
            />
          )}
          <div>
            {member.name && <Text fz={14}>{member.name}</Text>}
            {!isContributor && (
              <Text fz={14} c="dimmed">
                {member.email}
              </Text>
            )}
          </div>
        </Group>
      </Table.Td>
      <Table.Td>{displayRole}</Table.Td>
      <Table.Td className={styles.membertable_action}>
        <MemberAction member={member} />
      </Table.Td>
    </Table.Tr>
  )
})
